import cloneDeep from 'lodash/cloneDeep';
import * as PromiseAllSettled from './PromiseAllSettled';

// Bulk Set Options of One Customization
function checkShouldAttach(
  productItems,
  currentCustomization,
  originalAttachedItems,
  allItemCustomizationsAndProductIds,
  actions,
  apiToken,
) {
  const attachPromises = productItems.map(async (searchableItem) => {
    const itemId = searchableItem.value;
    if (!originalAttachedItems || originalAttachedItems.has(itemId)) return;

    if (!originalAttachedItems.has(itemId)) {
      return makeAttachRequest(
        currentCustomization,
        itemId,
        allItemCustomizationsAndProductIds,
        actions,
        apiToken,
      );
    }
  });

  return attachPromises;
}

function makeDetachRequest(
  itemId,
  customizationId,
  allItemCustomizationsAndProductIds,
  actions,
  apiToken,
) {
  const productId = allItemCustomizationsAndProductIds.get(itemId).get('productId');
  const customizations = allItemCustomizationsAndProductIds.get(itemId).get('customizations');

  const itemUpdatePath = ['products', productId, 'items', itemId];
  const customizationsClone = cloneDeep(customizations);
  const customizationUnattached = customizationsClone.filter(
    customization => customization.id !== Number(customizationId),
  );

  return actions.updateResource(apiToken, customizationUnattached, ...itemUpdatePath, 'customizations');
}

function checkShouldDetach(
  customizationId,
  originalAttachedItems,
  finalAttachedItemsIdMap,
  allItemCustomizationsAndProductIds,
  actions,
  apiToken,
) {
  const detachPromises = [];
  originalAttachedItems.forEach(async (val, itemId) => {
    if (finalAttachedItemsIdMap.has(itemId)) return;
    if (!finalAttachedItemsIdMap.has(itemId)) {
      const promise = makeDetachRequest(
        itemId,
        customizationId,
        allItemCustomizationsAndProductIds,
        actions,
        apiToken,
      );
      detachPromises.push(promise);
    }
  });
  return detachPromises;
}

function makeAttachRequest(
  currentCustomization,
  itemId,
  allItemCustomizationsAndProductIds,
  actions,
  apiToken,
) {
  const productId = allItemCustomizationsAndProductIds.get(itemId).get('productId');
  const itemUpdatePath = ['products', productId, 'items', itemId];
  const customizations = allItemCustomizationsAndProductIds.get(itemId).get('customizations');

  const customizationsClone = cloneDeep(customizations);

  customizationsClone.push(currentCustomization);
  return actions.updateResource(apiToken, customizationsClone, ...itemUpdatePath, 'customizations');
}

export async function bulkSetItemsOfOneCustomization({
  actions,
  apiToken,
  resourceId,
  originalAttachedItems,
  allItemCustomizationsAndProductIds,
  customization,
}) {
  if (!originalAttachedItems || !allItemCustomizationsAndProductIds || !resourceId) return;

  const { productItems, ...currentCustomization } = customization;
  const defaultedProductItems = productItems || [];
  const finalAttachedItemsIdMap = new Map();
  defaultedProductItems.forEach(
    searchableItem => finalAttachedItemsIdMap.set(searchableItem.value, true),
  );

  currentCustomization.id = Number(resourceId);

  const attachPromises = checkShouldAttach(
    defaultedProductItems,
    currentCustomization,
    originalAttachedItems,
    allItemCustomizationsAndProductIds,
    actions,
    apiToken,
  );
  const detachPromises = checkShouldDetach(
    resourceId,
    originalAttachedItems,
    finalAttachedItemsIdMap,
    allItemCustomizationsAndProductIds,
    actions,
    apiToken,
  );

  const results = await Promise.allSettled([...attachPromises, ...detachPromises]);
  PromiseAllSettled.logErrorResults(results, 'Error on FormSubmits.bulkSetItemsOfOneCustomization');
}

/**
 * The API takes an empty array as meaning all locations.
 * It is better to store an empty array in the database,
 * as otherwise, API fetches will add to the Redux store
 * a lot of extra locations objects to each product.
 */
export const prepareProductLocationsAsIds = (productLocations, allLocations) => {
  if (Array.isArray(productLocations) && Array.isArray(allLocations)
      && productLocations.length === allLocations.length) {
    return [];
  }
  return productLocations.map(location => location.id);
};

/**
 * The API takes an empty array as meaning all locations.
 * It is better to store an empty array in the database,
 * as otherwise, API fetches will add to the Redux store
 * a lot of extra locations objects stored on each product.
 */
export const useEmptyArrayIfAllLocations = (productLocations, allLocations) => {
  if (Array.isArray(productLocations) && Array.isArray(allLocations)
      && productLocations.length === allLocations.length) {
    return [];
  }
  return productLocations;
};
